import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert, Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Icon } from "@iconify/react";
import closeFill from "@iconify-icons/eva/close-fill";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { MIconButton } from "src/theme";

import { getCategoriesWithProducts, getCategorySuccess } from "src/redux/slices/categories";
import IsRole from "src/utils/IsRole";
import Roles from "src/utils/Roles";
import axios from "src/utils/axios";

const SimpleAddCategory = ({ onClose, onSelect }) => {
  const dispatch = useDispatch();
  const [openWarning, setOpenWarning] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { categories } = useSelector((state) => state.categories);
  const { claims } = useSelector((state) => state.authJwt);
  const isFranchisee = IsRole(claims.role, Roles.USER_FRANQUICICA);

  useEffect(() => {
    if (!categories || categories.length === 0) dispatch(getCategoriesWithProducts());
  }, []);

  const addCategory = async (data) => {
    const res = await axios.post("/categoria", JSON.stringify(data), {
      headers: { "Content-Type": "application/json" },
    });
    dispatch(getCategorySuccess(res.data.categoria));
    if (onSelect) onSelect(res.data.categoria.id);
    enqueueSnackbar("Categoría creada", {
      variant: "success",
      action: (key) => (
        <MIconButton size="small" onClick={() => closeSnackbar(key)}>
          <Icon icon={closeFill} />
        </MIconButton>
      ),
    });
    onClose();
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      displayName: "",
      visible: true,
      visibleToClients: true,
    },
    validationSchema: Yup.object().shape({
      name: Yup.string()
        .min(2, "Mínimo 2 caracteres")
        .max(50, "Máximo 50 caracteres")
        .required("El nombre es obligatorio"),
      displayName: Yup.string().max(100, "Máximo 100 caracteres"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const nameLower = values.name.toLowerCase();
        const alreadyExists =
          !openWarning &&
          categories?.find((c) => c.name.toLowerCase() === nameLower);

        if (alreadyExists) {
          setOpenWarning(true);
          setSubmitting(false);
          return;
        }

        setOpenWarning(false);
        await addCategory({
          Name: values.name,
          DisplayName: values.displayName,
          Visible: values.visible,
          VisibleToClients: values.visibleToClients,
          IdRestaurante: window.localStorage.getItem("userId"),
          IdCategory: null,
          Printer: -1,
          image: null,
        });
      } catch (error) {
        console.error(error);
        setSubmitting(false);
        enqueueSnackbar("Ha ocurrido algún error", {
          variant: "warning",
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          ),
        });
      }
    },
  });

  const { errors, values, touched, handleSubmit, isSubmitting, setFieldValue, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={2.5}>
          {/* Nombre interno */}
          <TextField
            fullWidth
            label="Nombre de la categoría"
            {...getFieldProps("name")}
            disabled={isFranchisee}
            error={Boolean(touched.name && errors.name)}
            helperText={touched.name && errors.name}
            autoFocus
          />

          {/* Nombre de carta */}
          <TextField
            fullWidth
            label="Nombre en carta (opcional)"
            {...getFieldProps("displayName")}
            disabled={isFranchisee}
            error={Boolean(touched.displayName && errors.displayName)}
            helperText={
              touched.displayName && errors.displayName
                ? errors.displayName
                : "Nombre visible para el cliente. Si está vacío se usa el nombre interno."
            }
          />

          {/* Visibilidad */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              backgroundColor: "grey.100",
              display: "flex",
              gap: 3,
              flexWrap: "wrap",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={values.visible}
                  onChange={(e) => {
                    setFieldValue("visible", e.target.checked);
                    if (!e.target.checked) setFieldValue("visibleToClients", false);
                  }}
                />
              }
              label={<Typography variant="body2">Visible</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={values.visibleToClients}
                  disabled={!values.visible}
                  {...getFieldProps("visibleToClients")}
                />
              }
              label={<Typography variant="body2">Visible para clientes</Typography>}
            />
          </Box>

          {/* Warning duplicado */}
          {openWarning && (
            <Alert
              severity="warning"
              action={
                <Button size="small" color="warning" onClick={handleSubmit}>
                  Crear igualmente
                </Button>
              }
            >
              Ya existe una categoría llamada <strong>{values.name}</strong>.
            </Alert>
          )}

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1.5, pt: 0.5 }}>
            <Button variant="outlined" color="inherit" fullWidth onClick={onClose}>
              Cancelar
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={isSubmitting}
            >
              Crear categoría
            </LoadingButton>
          </Box>
        </Stack>
      </Form>
    </FormikProvider>
  );
};

export default SimpleAddCategory;
